import { useCallback, useEffect, useRef, useState, type PointerEvent as RPointerEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, useApi, type ApiFailure } from '../lib/api.ts';
import { Empty, ErrorState, Icon, Loading, Sheet, can, orgPath, useOrg, useToast } from '../lib/ui.tsx';

type El =
  | {
      id: string;
      type: 'text';
      x: number;
      y: number;
      w: number;
      h: number;
      locked?: boolean;
      text: string;
      size: number;
      weight: '400' | '600' | '700' | '800';
      color: string;
      align: 'start' | 'middle' | 'end';
    }
  | {
      id: string;
      type: 'shape';
      x: number;
      y: number;
      w: number;
      h: number;
      locked?: boolean;
      shape: 'rect' | 'ellipse';
      fill: string;
      radius: number;
    }
  | {
      id: string;
      type: 'qr';
      x: number;
      y: number;
      w: number;
      h: number;
      locked?: boolean;
      url: string;
      fg: string;
      bg: string;
    }
  | {
      id: string;
      type: 'price';
      x: number;
      y: number;
      w: number;
      h: number;
      locked?: boolean;
      sku: string;
      size: number;
      color: string;
      prefix: string;
    }
  | {
      id: string;
      type: 'image';
      x: number;
      y: number;
      w: number;
      h: number;
      locked?: boolean;
      assetId: string;
      fit: 'contain' | 'cover';
    };
interface Doc {
  schemaVersion: 1;
  canvas: { width: number; height: number; background: string };
  safeAreaInset: number;
  elements: El[];
}
const GRID = 8;
const snap = (n: number) => Math.round(n / GRID) * GRID;

export function Studio() {
  const org = useOrg();
  const [params, setParams] = useSearchParams();
  const list = useApi<{
    designs: {
      id: string;
      name: string;
      version: number;
      canvas: Doc['canvas'];
      renderedAssetId: string | null;
    }[];
    templates: { id: string; name: string; canvas: Doc['canvas'] }[];
  }>(orgPath(org, '/designs'), [org.id]);
  const [creating, setCreating] = useState(false);
  const id = params.get('design');
  if (id) return <Editor id={id} onBack={() => (setParams({}), void list.reload())} />;
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Studio</h1>
          <p>
            Design signage in portrait, landscape or custom sizes. Designs render to identical pixels on every
            player.
          </p>
        </div>
        {can(org, 'assets.write') && (
          <button className="btn btn-primary" onClick={() => setCreating(true)}>
            <Icon name="plus" /> New design
          </button>
        )}
      </div>
      {list.error && <ErrorState error={list.error} />}
      {!list.data ? (
        <Loading />
      ) : !list.data.designs.length ? (
        <div className="card">
          <Empty
            title="No designs yet"
            action={
              <button className="btn btn-primary" onClick={() => setCreating(true)}>
                Start from a template
              </button>
            }
          />
        </div>
      ) : (
        <div className="grid grid-cards">
          {list.data.designs.map((d) => (
            <button
              key={d.id}
              className="card card-pad"
              style={{ textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'inherit' }}
              onClick={() => setParams({ design: d.id })}
            >
              <div
                className="thumb"
                style={{
                  aspectRatio: `${d.canvas.width} / ${d.canvas.height}`,
                  maxHeight: 180,
                  background: d.canvas.background,
                }}
              />
              <strong style={{ display: 'block', marginTop: 10 }}>{d.name}</strong>
              <span className="subtle">
                {d.canvas.width}×{d.canvas.height} · v{d.version}
                {d.renderedAssetId ? ' · rendered' : ''}
              </span>
            </button>
          ))}
        </div>
      )}
      {creating && list.data && (
        <NewDesign
          templates={list.data.templates}
          onClose={() => setCreating(false)}
          onCreated={(nid) => setParams({ design: nid })}
        />
      )}
    </>
  );
}

function NewDesign({
  templates,
  onClose,
  onCreated,
}: {
  templates: { name: string; canvas: Doc['canvas'] }[];
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const org = useOrg();
  const toast = useToast();
  const [name, setName] = useState('New design');
  const create = async (body: Record<string, unknown>) => {
    try {
      const r = await api<{ id: string }>('POST', orgPath(org, '/designs'), { name, ...body });
      onCreated(r.id);
    } catch (e) {
      toast((e as ApiFailure).message, 'danger');
    }
  };
  const blank = (w: number, h: number) => ({
    doc: {
      schemaVersion: 1,
      canvas: { width: w, height: h, background: '#FFFFFF' },
      safeAreaInset: 48,
      elements: [],
    },
  });
  return (
    <Sheet title="New design" onClose={onClose} wide>
      <div className="stack" style={{ gap: 16 }}>
        <div className="field">
          <label htmlFor="d-name">Name</label>
          <input id="d-name" className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="row">
          <button className="btn" onClick={() => void create(blank(1920, 1080))}>
            Blank landscape 16:9
          </button>
          <button className="btn" onClick={() => void create(blank(1080, 1920))}>
            Blank portrait 9:16
          </button>
          <button
            className="btn"
            onClick={() => {
              const w = Number(prompt('Width (320–3840)', '1280'));
              const h = Number(prompt('Height (320–3840)', '1280'));
              if (w && h) void create(blank(w, h));
            }}
          >
            Custom size…
          </button>
        </div>
        <h3>Templates</h3>
        <div className="grid grid-cards">
          {templates.map((t, i) => (
            <button
              key={t.name}
              className="card card-pad"
              style={{ cursor: 'pointer', textAlign: 'left', font: 'inherit', color: 'inherit' }}
              onClick={() => void create({ templateIndex: i })}
            >
              <div
                className="thumb"
                style={{
                  aspectRatio: `${t.canvas.width} / ${t.canvas.height}`,
                  maxHeight: 140,
                  background: t.canvas.background,
                }}
              />
              <strong style={{ display: 'block', marginTop: 8 }}>{t.name}</strong>
            </button>
          ))}
        </div>
      </div>
    </Sheet>
  );
}

function Editor({ id, onBack }: { id: string; onBack: () => void }) {
  const org = useOrg();
  const toast = useToast();
  const { data, error } = useApi<{
    design: { name: string; doc: Doc; version: number };
    versions: { version: number; createdAt: string }[];
    products: { sku: string; name: string; priceCents: number | null }[];
  }>(orgPath(org, `/designs/${id}`), [id]);
  const [doc, setDoc] = useState<Doc | null>(null);
  const [version, setVersion] = useState(0);
  const [sel, setSel] = useState<string | null>(null);
  const [past, setPast] = useState<Doc[]>([]);
  const [future, setFuture] = useState<Doc[]>([]);
  const [saveState, setSaveState] = useState('All changes saved');
  const drag = useRef<{ id: string; dx: number; dy: number; scale: number } | null>(null);
  const svg = useRef<SVGSVGElement>(null);
  useEffect(() => {
    if (data) {
      setDoc(data.design.doc);
      setVersion(data.design.version);
    }
  }, [data]);
  const change = useCallback((next: Doc) => {
    setDoc((cur) => {
      if (cur) setPast((p) => [...p.slice(-99), cur]);
      return next;
    });
    setFuture([]);
  }, []);
  const update = (elId: string, patch: Partial<El>) =>
    doc &&
    change({ ...doc, elements: doc.elements.map((e) => (e.id === elId ? ({ ...e, ...patch } as El) : e)) });
  const undo = () =>
    past.length &&
    doc &&
    (setFuture((f) => [doc, ...f]), setDoc(past[past.length - 1]!), setPast((p) => p.slice(0, -1)));
  const redo = () =>
    future.length && doc && (setPast((p) => [...p, doc]), setDoc(future[0]!), setFuture((f) => f.slice(1)));

  // Autosave (debounced) with optimistic concurrency.
  useEffect(() => {
    if (!doc || !data || doc === data.design.doc) return;
    setSaveState('Saving…');
    const t = setTimeout(async () => {
      try {
        const r = await api<{ version: number }>('PUT', orgPath(org, `/designs/${id}`), { doc, version });
        setVersion(r.version);
        setSaveState('All changes saved');
      } catch (e) {
        setSaveState((e as ApiFailure).message);
      }
    }, 900);
    return () => clearTimeout(t);
  }, [doc]); // eslint-disable-line react-hooks/exhaustive-deps

  const selected = doc?.elements.find((e) => e.id === sel) ?? null;
  // Keyboard: arrows nudge (Shift = 10x), Delete removes, Cmd/Ctrl+Z / Shift+Z undo/redo.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).closest('input, textarea, select')) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        e.shiftKey ? redo() : undo();
      }
      if (!selected || selected.locked) return;
      const step = e.shiftKey ? GRID * 10 : GRID;
      const moves: Record<string, [number, number]> = {
        ArrowLeft: [-step, 0],
        ArrowRight: [step, 0],
        ArrowUp: [0, -step],
        ArrowDown: [0, step],
      };
      if (moves[e.key]) {
        e.preventDefault();
        update(selected.id, { x: selected.x + moves[e.key]![0], y: selected.y + moves[e.key]![1] });
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        doc && change({ ...doc, elements: doc.elements.filter((x) => x.id !== selected.id) });
        setSel(null);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  if (error) return <ErrorState error={error} />;
  if (!doc || !data) return <Loading />;
  type NewEl = El extends infer T ? (T extends El ? Omit<T, 'id'> : never) : never;
  const add = (el: NewEl) => {
    const nid = `${el.type}-${Math.random().toString(36).slice(2, 7)}`;
    change({ ...doc, elements: [...doc.elements, { ...el, id: nid } as El] });
    setSel(nid);
  };
  const cx = doc.canvas.width / 2;
  const cy = doc.canvas.height / 2;
  const toCanvas = (ev: RPointerEvent) => {
    const r = svg.current!.getBoundingClientRect();
    const scale = doc.canvas.width / r.width;
    return { x: (ev.clientX - r.left) * scale, y: (ev.clientY - r.top) * scale, scale };
  };
  const price = (sku: string) => data.products.find((p) => p.sku === sku)?.priceCents;
  async function render() {
    try {
      await api('POST', orgPath(org, `/designs/${id}/render`), {});
      toast('Rendering… the image will appear in the Library, ready to add to a playlist.');
    } catch (e) {
      toast((e as ApiFailure).message, 'danger');
    }
  }
  return (
    <>
      <div className="page-head">
        <div>
          <div className="breadcrumb">
            <button className="btn btn-ghost btn-sm" onClick={onBack}>
              Studio
            </button>{' '}
            / {data.design.name}
          </div>
          <h1>{data.design.name}</h1>
          <p>
            {doc.canvas.width}×{doc.canvas.height} · version {version} ·{' '}
            <span aria-live="polite">{saveState}</span>
          </p>
        </div>
        <div className="row">
          <button className="btn" onClick={undo} disabled={!past.length} aria-label="Undo">
            Undo
          </button>
          <button className="btn" onClick={redo} disabled={!future.length} aria-label="Redo">
            Redo
          </button>
          <button className="btn btn-primary" onClick={() => void render()}>
            Render to Library
          </button>
        </div>
      </div>
      <div className="row" style={{ marginBottom: 12 }}>
        <span className="label">Add</span>
        <button
          className="btn btn-sm"
          onClick={() =>
            add({
              type: 'text',
              x: snap(cx - 400),
              y: snap(cy - 60),
              w: 800,
              h: 120,
              text: 'Headline',
              size: 96,
              weight: '700',
              color: '#1D1D1F',
              align: 'start',
            })
          }
        >
          Text
        </button>
        <button
          className="btn btn-sm"
          onClick={() =>
            add({
              type: 'shape',
              x: snap(cx - 200),
              y: snap(cy - 120),
              w: 400,
              h: 240,
              shape: 'rect',
              fill: '#0071E3',
              radius: 24,
            })
          }
        >
          Rectangle
        </button>
        <button
          className="btn btn-sm"
          onClick={() =>
            add({
              type: 'shape',
              x: snap(cx - 150),
              y: snap(cy - 150),
              w: 300,
              h: 300,
              shape: 'ellipse',
              fill: '#C01C38',
              radius: 0,
            })
          }
        >
          Ellipse
        </button>
        <button
          className="btn btn-sm"
          onClick={() =>
            add({
              type: 'qr',
              x: 64,
              y: 64,
              w: 240,
              h: 240,
              url: 'https://example.com',
              fg: '#000000',
              bg: '#FFFFFF',
            })
          }
        >
          QR code
        </button>
        <button
          className="btn btn-sm"
          onClick={() =>
            add({
              type: 'price',
              x: 64,
              y: snap(doc.canvas.height - 220),
              w: 600,
              h: 160,
              sku: data.products[0]?.sku ?? 'SKU',
              size: 120,
              color: '#1D1D1F',
              prefix: '$',
            })
          }
        >
          Price
        </button>
        <span className="subtle">
          Arrow keys nudge 8px (Shift: 80px). Live clock and ticker widgets need the player widget runtime and
          are not in this version.
        </span>
      </div>
      <div
        className="grid"
        style={{ gridTemplateColumns: '200px minmax(0, 1fr) 300px', alignItems: 'start' }}
      >
        <nav className="card card-pad stack" aria-label="Layers">
          <h3>Layers</h3>
          {[...doc.elements].reverse().map((e) => (
            <button
              key={e.id}
              className="nav-link"
              aria-current={e.id === sel ? 'page' : undefined}
              style={{
                border: 0,
                cursor: 'pointer',
                font: 'inherit',
                width: '100%',
                background: e.id === sel ? 'var(--action-soft)' : 'transparent',
              }}
              onClick={() => setSel(e.id)}
            >
              {e.type === 'text' ? `“${e.text.slice(0, 14)}”` : e.type}
              {e.locked ? ' 🔒' : ''}
            </button>
          ))}
        </nav>
        <div className="card" style={{ padding: 12, background: 'var(--surface-sunken)' }}>
          <svg
            ref={svg}
            viewBox={`0 0 ${doc.canvas.width} ${doc.canvas.height}`}
            style={{
              width: '100%',
              maxHeight: '70vh',
              display: 'block',
              background: doc.canvas.background,
              touchAction: 'none',
            }}
            role="img"
            aria-label="Design canvas (rendered preview)"
            onPointerMove={(ev) => {
              const d = drag.current;
              if (!d) return;
              const p = toCanvas(ev);
              setDoc(
                (cur) =>
                  cur && {
                    ...cur,
                    elements: cur.elements.map((e) =>
                      e.id === d.id ? ({ ...e, x: snap(p.x - d.dx), y: snap(p.y - d.dy) } as El) : e,
                    ),
                  },
              );
            }}
            onPointerUp={() => (drag.current = null)}
            onPointerDown={(ev) => ev.target === svg.current && setSel(null)}
          >
            {doc.elements.map((e) => {
              const common = {
                key: e.id,
                style: { cursor: e.locked ? 'not-allowed' : 'move' },
                onPointerDown: (ev: RPointerEvent) => {
                  ev.stopPropagation();
                  setSel(e.id);
                  if (e.locked) return;
                  const p = toCanvas(ev);
                  if (doc) setPast((x) => [...x, doc]);
                  drag.current = { id: e.id, dx: p.x - e.x, dy: p.y - e.y, scale: p.scale };
                },
              };
              if (e.type === 'shape')
                return e.shape === 'ellipse' ? (
                  <ellipse
                    {...common}
                    cx={e.x + e.w / 2}
                    cy={e.y + e.h / 2}
                    rx={e.w / 2}
                    ry={e.h / 2}
                    fill={e.fill}
                  />
                ) : (
                  <rect {...common} x={e.x} y={e.y} width={e.w} height={e.h} rx={e.radius} fill={e.fill} />
                );
              if (e.type === 'text')
                return (
                  <text
                    {...common}
                    x={e.align === 'middle' ? e.x + e.w / 2 : e.align === 'end' ? e.x + e.w : e.x}
                    y={e.y + e.size * 0.82}
                    fontSize={e.size}
                    fontWeight={Number(e.weight)}
                    fill={e.color}
                    textAnchor={e.align}
                    fontFamily="Helvetica, Arial, sans-serif"
                  >
                    {e.text}
                  </text>
                );
              if (e.type === 'price') {
                const c = price(e.sku);
                return (
                  <text
                    {...common}
                    x={e.x}
                    y={e.y + e.size * 0.82}
                    fontSize={e.size}
                    fontWeight={700}
                    fill={e.color}
                    fontFamily="Helvetica, Arial, sans-serif"
                  >
                    {c == null ? `${e.prefix}—` : `${e.prefix}${(c / 100).toFixed(2)}`}
                  </text>
                );
              }
              if (e.type === 'qr')
                return (
                  <g {...common}>
                    <rect x={e.x} y={e.y} width={e.w} height={e.h} fill={e.bg} />
                    <text
                      x={e.x + e.w / 2}
                      y={e.y + e.h / 2}
                      fontSize={Math.min(e.w, e.h) / 6}
                      textAnchor="middle"
                      fill={e.fg}
                    >
                      QR
                    </text>
                  </g>
                );
              return <rect {...common} x={e.x} y={e.y} width={e.w} height={e.h} fill="#ccc" />;
            })}
            <rect
              x={doc.safeAreaInset}
              y={doc.safeAreaInset}
              width={doc.canvas.width - 2 * doc.safeAreaInset}
              height={doc.canvas.height - 2 * doc.safeAreaInset}
              fill="none"
              stroke="#0071E3"
              strokeDasharray="12 10"
              strokeWidth={2}
              opacity={0.5}
              pointerEvents="none"
            />
            {selected && (
              <rect
                x={selected.x - 4}
                y={selected.y - 4}
                width={selected.w + 8}
                height={selected.h + 8}
                fill="none"
                stroke="#0071E3"
                strokeWidth={4}
                pointerEvents="none"
              />
            )}
          </svg>
          <p className="subtle" style={{ marginTop: 8 }}>
            Dashed line: safe area (keep important content inside). Rendered preview — the published image is
            rendered on the server.
          </p>
        </div>
        <aside className="card card-pad stack" aria-label="Properties">
          <h3>{selected ? `${selected.type} properties` : 'Canvas'}</h3>
          {!selected ? (
            <>
              <div className="field">
                <label htmlFor="bg">Background</label>
                <input
                  id="bg"
                  type="color"
                  className="input"
                  value={doc.canvas.background}
                  onChange={(e) =>
                    change({ ...doc, canvas: { ...doc.canvas, background: e.target.value.toUpperCase() } })
                  }
                />
              </div>
              <h3>History</h3>
              {data.versions.slice(0, 8).map((v) => (
                <span key={v.version} className="subtle">
                  v{v.version} · {new Date(v.createdAt).toLocaleTimeString()}
                </span>
              ))}
            </>
          ) : (
            <>
              {selected.locked && (
                <div className="alert info">Locked by the template. You can’t move or edit it.</div>
              )}
              <div className="row">
                {(['x', 'y', 'w', 'h'] as const).map((k) => (
                  <div key={k} className="field" style={{ width: 'calc(50% - 6px)' }}>
                    <label htmlFor={`p-${k}`}>{k.toUpperCase()}</label>
                    <input
                      id={`p-${k}`}
                      type="number"
                      className="input"
                      disabled={selected.locked}
                      value={selected[k]}
                      onChange={(e) =>
                        update(selected.id, {
                          [k]: Math.max(k === 'w' || k === 'h' ? 1 : -10000, Number(e.target.value)),
                        })
                      }
                    />
                  </div>
                ))}
              </div>
              {selected.type === 'text' && (
                <>
                  <div className="field">
                    <label htmlFor="p-text">Text</label>
                    <textarea
                      id="p-text"
                      className="input"
                      disabled={selected.locked}
                      value={selected.text}
                      maxLength={500}
                      onChange={(e) => update(selected.id, { text: e.target.value })}
                    />
                  </div>
                  <div className="row">
                    <div className="field">
                      <label htmlFor="p-size">Size</label>
                      <input
                        id="p-size"
                        type="number"
                        className="input"
                        value={selected.size}
                        onChange={(e) => update(selected.id, { size: Number(e.target.value) })}
                      />
                    </div>
                    <div className="field">
                      <label htmlFor="p-color">Color</label>
                      <input
                        id="p-color"
                        type="color"
                        className="input"
                        value={selected.color}
                        onChange={(e) => update(selected.id, { color: e.target.value.toUpperCase() })}
                      />
                    </div>
                  </div>
                  <div className="segmented" role="group" aria-label="Alignment">
                    {(['start', 'middle', 'end'] as const).map((a) => (
                      <button
                        key={a}
                        aria-pressed={selected.align === a}
                        onClick={() => update(selected.id, { align: a })}
                      >
                        {a}
                      </button>
                    ))}
                  </div>
                </>
              )}
              {selected.type === 'shape' && (
                <div className="field">
                  <label htmlFor="p-fill">Fill</label>
                  <input
                    id="p-fill"
                    type="color"
                    className="input"
                    value={selected.fill}
                    onChange={(e) => update(selected.id, { fill: e.target.value.toUpperCase() })}
                  />
                </div>
              )}
              {selected.type === 'qr' && (
                <div className="field">
                  <label htmlFor="p-url">Link (https only)</label>
                  <input
                    id="p-url"
                    className="input"
                    value={selected.url}
                    onChange={(e) => update(selected.id, { url: e.target.value })}
                  />
                </div>
              )}
              {selected.type === 'price' && (
                <div className="field">
                  <label htmlFor="p-sku">Product</label>
                  <select
                    id="p-sku"
                    className="input"
                    value={selected.sku}
                    onChange={(e) => update(selected.id, { sku: e.target.value })}
                  >
                    {data.products.map((p) => (
                      <option key={p.sku} value={p.sku}>
                        {p.sku} — {p.name}
                      </option>
                    ))}
                    {!data.products.length && <option>Import products in Integrations</option>}
                  </select>
                </div>
              )}
              {!selected.locked && (
                <div className="row">
                  <button
                    className="btn btn-sm"
                    onClick={() => update(selected.id, { x: snap((doc.canvas.width - selected.w) / 2) })}
                  >
                    Center horizontally
                  </button>
                  <button
                    className="btn btn-sm"
                    onClick={() => update(selected.id, { y: snap((doc.canvas.height - selected.h) / 2) })}
                  >
                    Center vertically
                  </button>
                  <button
                    className="btn btn-sm"
                    onClick={() =>
                      change({
                        ...doc,
                        elements: [...doc.elements.filter((x) => x.id !== selected.id), selected],
                      })
                    }
                  >
                    Bring to front
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => (
                      change({ ...doc, elements: doc.elements.filter((x) => x.id !== selected.id) }),
                      setSel(null)
                    )}
                  >
                    Delete
                  </button>
                </div>
              )}
            </>
          )}
        </aside>
      </div>
    </>
  );
}
